import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Layout, Typography, DatePicker, Input, Button, Table, Form, InputNumber, Row, Col, Tag, Select, Modal, Spin } from 'antd';
import { PlusOutlined, CloseOutlined, FileTextOutlined, DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import '../styles/AdminAcknowledgementReceipt.css';
import '../styles/SharedLayout.css';
import AdminSidebar from '../components/AdminSidebar';
import { postRequest, getRequest } from '../utilities/apicalls';
import ToastMessage from '../components/ToastMessage';

const { Content } = Layout;
const { Title } = Typography;

/**
 * AdminAcknowledgementReceipt Component
 * 
 * Displays and manages scholar acknowledgment receipts with filtering capabilities
 * @returns {JSX.Element} The AdminAcknowledgementReceipt component
 */
const AdminAcknowledgementReceipt = () => {
  // State for filters and pagination
  const [dateRange, setDateRange] = useState([null, null]);
  const [searchScholar, setSearchScholar] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scholarsLoading, setScholarsLoading] = useState(false);
  const [allReceipts, setAllReceipts] = useState([]);
  const [filteredReceipts, setFilteredReceipts] = useState([]);
  const [filtersActive, setFiltersActive] = useState(false);
  const [scholars, setScholars] = useState([]);
  const [form] = Form.useForm();
  const receiptRef = useRef(null);
  
  // Add responsive state variables for mobile detection
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 425);
  const [isSmallMobile, setIsSmallMobile] = useState(window.innerWidth <= 375);
  const [isExtraSmallMobile, setIsExtraSmallMobile] = useState(window.innerWidth <= 320);
  
  // Refs for controlling API requests
  const scholarFetchInProgress = useRef(false);
  const lastScholarFetchTime = useRef(0);
  const scholarsCache = useRef(null);
  
  // Cache duration in milliseconds (5 minutes)
  const CACHE_DURATION = 5 * 60 * 1000;

  // Add resize event handler to update responsive states
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 425);
      setIsSmallMobile(window.innerWidth <= 375);
      setIsExtraSmallMobile(window.innerWidth <= 320);
    };
    
    window.addEventListener('resize', handleResize);
    
    // Initial check
    handleResize();
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Memoize fetchScholars to prevent dependency cycle in useEffect
  const memoizedFetchScholars = useCallback(async (forceRefresh = false) => {
    // Prevent multiple simultaneous requests
    if (scholarFetchInProgress.current) {
      return scholarsCache.current || [];
    }
    
    // Check if we have a recent cache and don't need to force refresh
    const now = Date.now();
    if (!forceRefresh && 
        scholarsCache.current && 
        lastScholarFetchTime.current > 0 && 
        (now - lastScholarFetchTime.current) < CACHE_DURATION) {
      return scholarsCache.current;
    }
    
    try {
      // Set flags to prevent duplicate requests
      scholarFetchInProgress.current = true;
      setScholarsLoading(true);
      
      // Get authentication data
      const token = localStorage.getItem('token');
      const id = localStorage.getItem('id');

      if (!token || !id) {
        ToastMessage.error('Authentication data not found. Please log in again.');
        setScholarsLoading(false);
        scholarFetchInProgress.current = false;
        return [];
      }

      // Make direct API call to fetch scholars
      const response = await getRequest('scholars/get', {
        token: token,
        requester: id,
        screen: 'user',
        status: 'Active'
      });

      // Handle different response formats
      if (response && response.data) {
        let scholarsData;
        
        // Check if response.data is already an array
        if (Array.isArray(response.data)) {
          scholarsData = response.data;
        } 
        // Check if response.data has a nested data property
        else if (response.data.data && Array.isArray(response.data.data)) {
          scholarsData = response.data.data;
        } 
        // If it's a single object, convert to array
        else if (typeof response.data === 'object') {
          scholarsData = [response.data];
        } 
        else {
          scholarsData = [];
        }
        
        // Format the scholar data for the dropdown
        const formattedScholars = scholarsData
          .filter(scholar => scholar != null)
          .map(scholar => {
            // Ensure each scholar has the necessary properties
            const scholarId = scholar.id || scholar.scholar_id;
            const firstName = scholar.first_name || '';
            const middleName = scholar.middle_name ? scholar.middle_name + ' ' : '';
            const lastName = scholar.last_name || '';
            const fullName = `${firstName} ${middleName}${lastName}`.trim();
            
            return {
              scholar_id: scholarId,
              key: scholarId,
              name: fullName || scholar.name || 'Unknown Scholar',
              firstName,
              lastName,
              email: scholar.email || '',
              course: scholar.course || '',
              scholarType: scholar.scholar_type || ''
            };
          })
          .filter(scholar => scholar.scholar_id);  // Only keep scholars with IDs
        
        // Save to localStorage for future use
        localStorage.setItem('scholars', JSON.stringify(formattedScholars));
        setScholars(formattedScholars);
        
        // Update cache and timestamp
        scholarsCache.current = formattedScholars;
        lastScholarFetchTime.current = Date.now();
        
        setScholarsLoading(false);
        scholarFetchInProgress.current = false;
        return formattedScholars;
      }

      setScholarsLoading(false);
      scholarFetchInProgress.current = false;
      return [];
    } catch (error) {
      ToastMessage.error('Failed to load scholars. Please try again.');
      setScholarsLoading(false);
      scholarFetchInProgress.current = false;
      return [];
    }
  }, [CACHE_DURATION]); // Add CACHE_DURATION to dependency array

  // Modified fetchReceipts to use cached scholars when possible
  const fetchReceipts = useCallback(async (dates = dateRange) => {
    try {
      setLoading(true);
      
      // Get authentication data from localStorage following ScholarAcknowledgementReceipt pattern
      const token = localStorage.getItem('token');
      const id = localStorage.getItem('id');
      const tokenExpiry = localStorage.getItem('tokenExpiry');
      
      if (!token || !id) {
        ToastMessage.error('Authentication data not found. Please log in again.');
        setLoading(false);
        return;
      }
      
      // Check if token is expired
      if (tokenExpiry && new Date(tokenExpiry) < new Date()) {
        // Token expired, redirect to login
        localStorage.removeItem('token');
        localStorage.removeItem('id');
        localStorage.removeItem('tokenExpiry');
        ToastMessage.error('Your session has expired. Please log in again.');
        window.location.href = '/login';
        return;
      }
      
      // Only fetch scholars if we don't already have them cached
      const currentScholars = scholarsCache.current || await memoizedFetchScholars();
      
      // Build params for GET request
      const params = {
        screen: 'user',
        token: token,
        requester: id
      };
      
      // Add date range parameters if they exist and are valid
      const hasValidDateRange = dates && dates[0] && dates[1];
      if (hasValidDateRange) {
        params.date_from = dates[0].format('YYYY-MM-DD');
        params.date_to = dates[1].format('YYYY-MM-DD');
      }
      
      // Use getRequest instead of postRequest for receipts/get
      const response = await getRequest('receipts/get', params);
      if (response.success && response.data) {
        let dataToProcess = response.data;
        
        // Handle the response data
        // Convert single object to array if needed
        if (!Array.isArray(dataToProcess)) {
          if (dataToProcess.data) {
            // If response has a data property
            dataToProcess = Array.isArray(dataToProcess.data) ? dataToProcess.data : [dataToProcess.data];
          } else {
            // If response is a single object
            dataToProcess = [dataToProcess];
          }
        }
        
        // Filter out any null or undefined items
        dataToProcess = dataToProcess.filter(item => item != null);

        // Format the receipt data
        const formattedReceipts = dataToProcess.map(item => {
          // Look up scholar name from scholar_id if available
          let scholarName = item.scholar_name || item.name || 'N/A';
          let scholarId = item.scholar_id || '';
          
          // If we have scholar_id but no name, try to find it from the scholars array
          if ((scholarName === 'N/A' || !scholarName) && scholarId) {
            // Try different formats of scholar_id (string vs number)
            const matchingScholar = currentScholars.find(s => 
              s.scholar_id.toString() === scholarId.toString());
            
            if (matchingScholar) {
              scholarName = matchingScholar.name;
            }
          }
          
          return {
            key: item.id || item.receipt_id || Math.random().toString(36).substring(2),
            id: item.id || item.receipt_id || Math.random().toString(36).substring(2),
            name: scholarName,
            scholar_id: scholarId,
            date_received: item.date_received || item.date || 
                        (item.created_at ? dayjs(item.created_at).format('MMMM D, YYYY') : dayjs().format('MMMM D, YYYY')),
            amount: item.amount || 'N/A',
            date: item.updated_on ? dayjs(item.updated_on).format('MMM DD, YYYY h:mm A') : '',
            status: item.status || (item.signature ? 'Signed' : 'For Signature'),
            type: item.description || item.type || item.receipt_type || 'Monthly Allowance',
            declaration: item.declaration || 
                     `I hereby acknowledge the receipt of my scholarship allowance for ${dayjs(item.date_received || item.created_at || new Date()).format('MMMM D, YYYY')}. I declare that all information provided is true and correct to the best of my knowledge.`,
            signature: item.signature || null,
            scholarDetails: {
              name: scholarName,
              email: item.scholar_email || item.email || 'N/A',
              course: item.course || 'N/A',
              scholarType: item.scholar_type || item.type || 'N/A'
            }
          };
        });
        
        setAllReceipts(formattedReceipts);
        setFilteredReceipts(formattedReceipts);
        
        // Save receipts to localStorage with full scholar information
        localStorage.setItem('receipts', JSON.stringify(formattedReceipts));
      } else {
        setAllReceipts([]);
        setFilteredReceipts([]);
      }
      
      setLoading(false);
    } catch (error) {
      setAllReceipts([]);
      setFilteredReceipts([]);
      setLoading(false);
    }
  }, [dateRange, memoizedFetchScholars]); // Add dependencies

  // Load receipts from API on initial load, with localStorage as backup
  useEffect(() => {
    // Only fetch scholars once on component mount, then receipts
    memoizedFetchScholars().then(() => {
      // After fetching scholars, fetch receipts
      fetchReceipts().catch(error => {
        // Fall back to localStorage if API fails
        const savedReceipts = localStorage.getItem('receipts');
        if (savedReceipts) {
          try {
            const parsedReceipts = JSON.parse(savedReceipts);
            
            // Update scholar names if we have scholar data
            if (scholarsCache.current && scholarsCache.current.length > 0) {
              const updatedReceipts = parsedReceipts.map(receipt => {
                // If receipt already has a non-N/A name, keep it
                if (receipt.name && receipt.name !== 'N/A') {
                  return receipt;
                }
                
                // Otherwise try to match with scholar data
                if (receipt.scholar_id) {
                  const matchingScholar = scholarsCache.current.find(s => 
                    s.scholar_id.toString() === receipt.scholar_id.toString());
                  
                  if (matchingScholar) {
                    return {
                      ...receipt,
                      name: matchingScholar.name,
                      scholarDetails: {
                        ...receipt.scholarDetails,
                        name: matchingScholar.name
                      }
                    };
                  }
                }
                
                return receipt;
              });
              
              setAllReceipts(updatedReceipts);
              setFilteredReceipts(updatedReceipts);
              
              // Update localStorage with refreshed scholar names
              localStorage.setItem('receipts', JSON.stringify(updatedReceipts));
            } else {
              setAllReceipts(parsedReceipts);
              setFilteredReceipts(parsedReceipts);
            }
          } catch (error) {
            // Fall back to empty arrays if parsing fails
            setAllReceipts([]);
            setFilteredReceipts([]);
          }
        }
      });
    });
  }, [memoizedFetchScholars, fetchReceipts]); // Include fetchReceipts in dependency array

  // Ensure scholars are loaded when modal opens - use caching
  useEffect(() => {
    if (isModalVisible && (!scholarsCache.current || !scholarsCache.current.length)) {
      memoizedFetchScholars();
    }
  }, [isModalVisible, memoizedFetchScholars]);

  // Only update filtered receipts when all receipts change
  useEffect(() => {
    // Automatically apply current filters when allReceipts changes
    if (searchScholar.trim() !== '' || (dateRange && dateRange[0] && dateRange[1])) {
      applyFilters();
    } else {
      setFilteredReceipts(allReceipts);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allReceipts, searchScholar, dateRange]);

  // Apply filters to the receipts
  const applyFilters = (dates = dateRange, search = searchScholar) => {
    setLoading(true);
    
    // Only set filtersActive if we actually have filters
    const hasDateFilter = dates && dates[0] && dates[1];
    const hasSearchFilter = search && search.trim() !== '';
    setFiltersActive(hasDateFilter || hasSearchFilter);
    
    try {
      // Start with all receipts
      let result = [...allReceipts];
      
      // Filter by scholar name if search term exists
      if (hasSearchFilter) {
        const searchTerm = search.toLowerCase();
        result = result.filter(receipt => 
          receipt.name.toLowerCase().includes(searchTerm)
        );
      }
      
      // Only apply date filtering client-side if we haven't already fetched with date filter from API
      if (hasDateFilter) {
        const [startDate, endDate] = dates;
        
        // Convert to simplified date strings for easier comparison 
        const startDateStr = startDate.format('YYYY-MM-DD');
        const endDateStr = endDate.format('YYYY-MM-DD');
        
        result = result.filter(receipt => {
          // Try to parse date_received first
          if (receipt.date_received) {
            const receivedDate = dayjs(receipt.date_received);
            const receivedDateStr = receivedDate.format('YYYY-MM-DD');
            
            // Check if it's a valid date and if it falls within the range
            if (receivedDate.isValid()) {
              const isInRange = 
                (receivedDateStr >= startDateStr && receivedDateStr <= endDateStr);
              
              if (isInRange) {
                return true;
              }
            }
          }
          
          // If no match on date_received, try the signed date
          if (receipt.date) {
            const signedDate = dayjs(receipt.date);
            const signedDateStr = signedDate.format('YYYY-MM-DD');
            
            // Check if it's a valid date and if it falls within the range
            if (signedDate.isValid()) {
              const isInRange = 
                (signedDateStr >= startDateStr && signedDateStr <= endDateStr);
              
              if (isInRange) {
                return true;
              }
            }
          }
          
          // If no valid dates or dates are outside range, exclude the receipt
          return false;
        });
      }
      
      setFilteredReceipts(result);
      // Reset to first page when filters change
      setCurrentPage(1);
    } catch (error) {
      // Handle error silently
    } finally {
      setLoading(false);
    }
  };

  // Handle scholar search input change with debounce
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchScholar(value);
    
    // If search field is cleared, and no filters are active, reset to show all
    if (value === '' && (!dateRange || !dateRange[0] || !dateRange[1])) {
      setFiltersActive(false);
      setFilteredReceipts(allReceipts);
    } else {
      // Automatically apply filters
      setFiltersActive(true);
      applyFilters(dateRange, value);
    }
  };

  // Handle date selection
  const handleDateChange = (dates) => {
    setDateRange(dates);
    
    // If dates are cleared (x button clicked), reset and refetch all data
    if (!dates || dates === null || !Array.isArray(dates) || dates.length === 0 || !dates[0] || !dates[1]) {
      // Reset date range in state
      setDateRange([null, null]);
      
      // Only keep search filter active if it exists
      setFiltersActive(searchScholar.trim() !== '');
      
      // Refetch all receipts from API without date filter
      fetchReceipts(null).then(() => {
        // After fetching, apply any search filters if needed
        if (searchScholar.trim() !== '') {
          applyFilters(null, searchScholar);
        }
      });
    } else {
      // Automatically apply filters when date changes
      setFiltersActive(true);
      
      // Fetch from API with date range
      fetchReceipts(dates).then(() => {
        // After fetching, apply any search filters if needed
        if (searchScholar.trim() !== '') {
          applyFilters(dates, searchScholar);
        }
      });
    }
  };

  // Table columns configuration
  const columns = [
    {
      title: 'Scholar Name',
      dataIndex: 'name',
      key: 'name',
      align: 'left',
      onHeaderCell: () => ({
        className: 'text-left-header'
      }),
      render: (text, record) => (
        <span onClick={() => handleRowClick(record)} style={{ cursor: 'pointer', display: 'block', width: '100%' }}>{text}</span>
      )
    },
    {
      title: 'Description',
      dataIndex: 'type',
      key: 'type',
      align: 'left',
      onHeaderCell: () => ({
        className: 'text-left-header'
      }),
      render: (text, record) => (
        <span onClick={() => handleRowClick(record)} style={{ cursor: 'pointer', display: 'block', width: '100%' }}>{text || 'Monthly Allowance'}</span>
      )
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      align: 'right',
      onHeaderCell: () => ({
        className: 'text-right-header'
      }),
      render: (amount, record) => (
        <span onClick={() => handleRowClick(record)} style={{ cursor: 'pointer', display: 'block', width: '100%', textAlign: 'right' }}>{formatCurrency(amount)}</span>
      )
    },
    {
      title: 'Date Received',
      dataIndex: 'date_received',
      key: 'date_received',
      align: 'left',
      onHeaderCell: () => ({
        className: 'text-left-header'
      }),
      render: (text, record) => {
        // Check if date_received has time component (contains 'T' or ':')
        let dateDisplay;
        if (record.date_received && (record.date_received.includes('T') || record.date_received.includes(':'))) {
          // Format with time component
          dateDisplay = <span className="nowrap-date">{dayjs(record.date_received).format('MMMM D, YYYY h:mm A')}</span>;
        } else if (record.date_received) {
          // Try to parse the date and format it consistently
          const parsedDate = dayjs(record.date_received);
          if (parsedDate.isValid()) {
            dateDisplay = <span className="nowrap-date">{parsedDate.format('MMMM D, YYYY')}</span>;
          } else {
            dateDisplay = record.date_received || 'N/A';
          }
        } else {
          dateDisplay = 'N/A';
        }
        
        return (
          <span onClick={() => handleRowClick(record)} style={{ cursor: 'pointer', display: 'block', width: '100%' }}>
            {dateDisplay}
          </span>
        );
      }
    },
    {
      title: 'Signed on',
      dataIndex: 'date',
      key: 'date',
      align: 'left',
      onHeaderCell: () => ({
        className: 'text-left-header'
      }),
      render: (text, record) => {
        let signedDate;
        if (record.status === 'For Signature') {
          signedDate = '';
        } else if (text) {
          // Try to parse the date and format it consistently
          const parsedDate = dayjs(text);
          if (parsedDate.isValid()) {
            // Include time component for Signed on date
            signedDate = <span className="nowrap-date">{parsedDate.format('MMMM D, YYYY h:mm A')}</span>;
          } else {
            signedDate = text;
          }
        } else {
          signedDate = '';
        }
        
        return (
          <span onClick={() => handleRowClick(record)} style={{ cursor: 'pointer', display: 'block', width: '100%' }}>
            {signedDate}
          </span>
        );
      }
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      align: 'center',
      render: (status, record) => {
        const normalizedStatus = String(status || '').trim();
        
        let statusTag;
        if (normalizedStatus === 'Signed') {
          statusTag = (
            <Tag color="success" style={{ padding: '4px 8px' }}>
              Signed
            </Tag>
          );
        } else if (normalizedStatus === 'For Signature') {
          statusTag = (
            <Tag color="orange" style={{ padding: '4px 8px' }}>
              For Signature
            </Tag>
          );
        } else {
          statusTag = (
            <Tag color="default" style={{ padding: '4px 8px' }}>
              {status}
            </Tag>
          );
        }
        
        return (
          <div onClick={() => handleRowClick(record)} style={{ cursor: 'pointer', display: 'flex', justifyContent: 'center', width: '100%' }}>
            {statusTag}
          </div>
        );
      }
    }
  ];

  const handleRowClick = (record) => {
    setSelectedReceipt(record);
    setIsViewModalVisible(true);
  };

  const handleDelete = async (record) => {
    try {
      // Get authentication data from localStorage
      const token = localStorage.getItem('token');
      const id = localStorage.getItem('id');
      const tokenExpiry = localStorage.getItem('tokenExpiry');
      
      if (!token || !id) {
        ToastMessage.error('Authentication data not found. Please log in again.');
        return;
      }
      
      // Check if token is expired
      if (tokenExpiry && new Date(tokenExpiry) < new Date()) {
        // Token expired, redirect to login
        localStorage.removeItem('token');
        localStorage.removeItem('id');
        localStorage.removeItem('tokenExpiry');
        ToastMessage.error('Your session has expired. Please log in again.');
        window.location.href = '/login';
        return;
      }
      
      // Create FormData object for the API request
      const formData = new FormData();
      formData.append('receipt_id', record.key);
      formData.append('token', token);
      formData.append('requester', id);
      
      // Use postRequest from apicalls.jsx with FormData flags
      const response = await postRequest('receipts/delete', formData, true, true);
      
      if (response.status === 200) {
        // Remove from all receipts
        setAllReceipts(prev => prev.filter(item => item.key !== record.key));
        // Apply filters again to update filtered list
        if (filtersActive) {
          applyFilters();
        } else {
          setFilteredReceipts(prev => prev.filter(item => item.key !== record.key));
        }
        
        setIsViewModalVisible(false);
        ToastMessage.success('Receipt deleted successfully');

        // Remove from localStorage if present
        const savedReceipts = localStorage.getItem('receipts');
        if (savedReceipts) {
          try {
            const parsedReceipts = JSON.parse(savedReceipts);
            const updatedReceipts = parsedReceipts.filter(item => item.key !== record.key);
            localStorage.setItem('receipts', JSON.stringify(updatedReceipts));
          } catch (error) {
            // Handle silently
          }
        }
      } else {
        // Handle non-200 response
        const errorMessage = response?.messages?.error_messages?.[0] || 'Failed to delete receipt';
        ToastMessage.error(errorMessage);
      }
    } catch (error) {
      // Handle API errors using our consistent pattern
      const errorMessage = error?.messages?.error_messages?.[0] || 'Failed to delete receipt';
      ToastMessage.error(errorMessage);
    }
  };

  const handleDownloadPDF = async () => {
    if (!receiptRef.current) return;

    try {
      // Show loading message
      ToastMessage.loading({ content: 'Generating PDF...', key: 'pdfLoading' });
      
      const receiptElement = receiptRef.current;
      
      // Temporarily hide any buttons in the receipt for cleaner PDF
      const allButtons = receiptElement.querySelectorAll('button');
      allButtons.forEach(btn => {
        btn.style.display = 'none';
      });
      
      // Use html2canvas with simpler configuration
      const canvas = await html2canvas(receiptElement, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });
      
      // Restore buttons visibility
      allButtons.forEach(btn => {
        btn.style.display = '';
      });
      
      const imgData = canvas.toDataURL('image/png');
      
      // Create a custom size PDF that's appropriate for a receipt
      // Use a portrait orientation with custom dimensions
      const pdf = new jsPDF('p', 'mm', [210, 297]); // A4 size
      
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      
      // Calculate ratio to fit the image properly in the PDF
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight) * 0.9; // 90% of max size
      
      // Center the image on the page
      const imgX = (pdfWidth - imgWidth * ratio) / 2;
      const imgY = 20; // 20mm from top
      
      pdf.addImage(imgData, 'PNG', imgX, imgY, imgWidth * ratio, imgHeight * ratio);
      pdf.save(`Acknowledgement_Receipt_${selectedReceipt?.name || 'Scholar'}.pdf`);
      
      // Success message
      ToastMessage.success({ content: 'PDF downloaded successfully!', key: 'pdfLoading', duration: 2 });
    } catch (error) {
      ToastMessage.error({ content: 'Failed to generate PDF', key: 'pdfLoading', duration: 2 });
    }
  };

  const handleSidebarCollapse = (collapsed) => {
    setSidebarCollapsed(collapsed);
  };

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    // Just reset form and state - let Ant Design handle the modal cleanup
    form.resetFields();
    setIsModalVisible(false);
  };

  const handleViewModalCancel = () => {
    // Just reset state - let Ant Design handle the modal cleanup
    setIsViewModalVisible(false);
    setSelectedReceipt(null);
  };

  const handleFormSubmit = async (values) => {
    try {
      setLoading(true);
      
      // Get authentication data from localStorage
      const token = localStorage.getItem('token');
      const id = localStorage.getItem('id');
      const tokenExpiry = localStorage.getItem('tokenExpiry');
      
      if (!token || !id) {
        ToastMessage.error('Authentication data not found. Please log in again.');
        setLoading(false);
        return;
      }
      
      // Check if token is expired
      if (tokenExpiry && new Date(tokenExpiry) < new Date()) {
        // Token expired, redirect to login
        localStorage.removeItem('token');
        localStorage.removeItem('id');
        localStorage.removeItem('tokenExpiry');
        ToastMessage.error('Your session has expired. Please log in again.');
        window.location.href = '/login';
        setLoading(false);
        return;
      }
      
      if (!values.dateReceived) {
        ToastMessage.error('Date received is required');
        setLoading(false);
        return;
      }
      
      // Create a proper FormData object for multipart/form-data request
      const formData = new FormData();
      formData.append('token', token);
      formData.append('requester', id);
      formData.append('scholar_id', values.scholar_id);
      formData.append('date_received', values.dateReceived.format('YYYY-MM-DD'));
      formData.append('amount', values.amount);
      formData.append('description', values.description);
      
      // Store values before API call to reference later
      const submittedValues = { ...values };
      const selectedScholar = scholars.find(s => s.scholar_id === values.scholar_id);
      const scholarName = selectedScholar?.name || 'Unknown Scholar';
      
      // Use the actual FormData object with isFormData: true
      const response = await postRequest('receipts/create', formData, true, true);
      
      if (response.status === 200) {
        // Display success message
        ToastMessage.success('Receipt added successfully!');
        
        // Reset form and close modal - let Ant Design handle the cleanup
        form.resetFields();
        setIsModalVisible(false);
        
        // Create a small delay before updating the receipt list
        // to ensure modal is fully closed first
        setTimeout(() => {
          // Create receipt object
          const newReceipt = {
            key: response.data?.receipt_id || Date.now().toString(),
            name: scholarName,
            date: '',
            date_received: (() => {
              const selectedDate = submittedValues.dateReceived.format('YYYY-MM-DD');
              const currentTime = dayjs().format('HH:mm:ss');
              return `${selectedDate} ${currentTime}`;
            })(),
            status: 'For Signature',
            avatar: null,
            amount: submittedValues.amount,
            description: submittedValues.description,
            scholar_id: submittedValues.scholar_id
          };
          
          // Add to receipts list
          addReceipt(newReceipt);
          
          // Fetch updated receipts after a delay
          setTimeout(() => {
            fetchReceipts();
            setLoading(false);
            
            // Ensure scrolling is restored
            document.body.style.overflow = '';
            window.scrollTo(0, 0);
          }, 300);
        }, 100);
      } else {
        // Handle non-200 response
        const errorMessage = response?.messages?.error_messages?.[0] || 'Failed to add receipt';
        ToastMessage.error(errorMessage);
      }
    } catch (error) {
      // Handle API errors using our consistent pattern
      const errorMessage = error?.messages?.error_messages?.[0] || 'Failed to add receipt';
      ToastMessage.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Format number to currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP',
      minimumFractionDigits: 2
    }).format(amount).replace('PHP', 'PHP');
  };

  // Add new receipt to the list
  const addReceipt = (newReceipt) => {
    setAllReceipts(prevReceipts => {
      const updatedReceipts = [...prevReceipts, newReceipt];
      // Update the filtered receipts if no filters are active, otherwise let applyFilters handle it
      if (!filtersActive) {
        setFilteredReceipts(updatedReceipts);
      } else {
        // If filters are active, run the filter logic
        applyFilters();
      }
      return updatedReceipts;
    });
  };

  // Save receipts to localStorage whenever they change
  useEffect(() => {
    if (allReceipts.length > 0) {
      localStorage.setItem('receipts', JSON.stringify(allReceipts));
    }
  }, [allReceipts]);

  return (
    <Layout className="admin-layout">
      <AdminSidebar 
        activePage="acknowledgments" 
        onCollapse={handleSidebarCollapse}
      />
      <Content className={`admin-content ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        <div className="admin-receipt-container">
          <Title level={4}>Acknowledgement Receipt Management</Title>

          {/* Filters Section */}
          <div className="filters-section">
            <DatePicker.RangePicker
              value={dateRange}
              onChange={handleDateChange}
              className="date-picker"
              format="YYYY-MM-DD"
              allowClear={true}
              placeholder={['Date From', 'Date To']}
            />

            <Input
              placeholder="Search by name or email"
              value={searchScholar || ''}
              onChange={handleSearchChange}
              className="scholar-search"
              prefix={<SearchOutlined />}
              allowClear
              onClear={() => {
                setFilteredReceipts(allReceipts);
              }}
            />

            <Button 
              type="primary" 
              icon={<PlusOutlined />} 
              onClick={showModal}
              className="add-button"
            >
              Create AR
            </Button>
          </div>

          {/* Table Section */}
          <Table
            columns={columns}
            dataSource={filteredReceipts}
            loading={loading}
            pagination={{
              current: currentPage,
              total: filteredReceipts.length,
              pageSize: pageSize,
              onChange: setCurrentPage,
              showSizeChanger: true,
              pageSizeOptions: [5, 10, 20, filteredReceipts.length.toString()],
              responsive: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
              position: ['bottomCenter'],
              onShowSizeChange: (current, size) => {
                setCurrentPage(1); // Reset to first page when changing page size
                setPageSize(size);
              },
              locale: {
                items_per_page: "/ page",
              },
              itemRender: (page, type, element) => {
                if (type === 'page') {
                  return <button type="button" style={{ border: 'none', background: 'transparent', cursor: 'pointer' }}>{page}</button>;
                }
                return element;
              }
            }}
            className="receipts-table"
            onRow={(record) => ({
              onClick: () => handleRowClick(record),
              style: { cursor: 'pointer' }
            })}
            locale={{ 
              emptyText: 'No receipts found',
              Pagination: {
                items_per_page: '/ page',
                jump_to: 'Go to',
                page: 'Page',
                prev_page: 'Previous Page',
                next_page: 'Next Page',
                prev_5: 'Previous 5 Pages',
                next_5: 'Next 5 Pages'
              }
            }}
          />

          {/* Add Receipt Modal */}
          <Modal
            title="Add Acknowledgement Receipt"
            open={isModalVisible}
            onCancel={handleCancel}
            footer={null}
            closeIcon={<CloseOutlined />}
            className="receipt-modal"
            style={{ 
              top: '50%',
              transform: 'translateY(-50%)'
            }}
            destroyOnClose={true}
            forceRender={false}
            maskClosable={true}
            keyboard={true}
            getContainer={document.body}
            afterOpenChange={(visible) => {
              if (visible && (!scholarsCache.current || !scholarsCache.current.length)) {
                // Only fetch fresh scholar data when modal opens if we don't have cached data
                memoizedFetchScholars();
              }
            }}
          >
            <div style={{ maxHeight: 'calc(80vh - 120px)', overflowY: 'auto', paddingRight: '5px' }}>
              <Form
                form={form}
                layout="vertical"
                onFinish={handleFormSubmit}
                preserve={false}
              >
                <Row gutter={16}>
                  <Col span={24}>
                    <Form.Item
                      name="scholar_id"
                      label="Scholar"
                      rules={[{ required: true, message: 'Please select a scholar' }]}
                    >
                      <Select
                        placeholder="Select a scholar"
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                          option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                        loading={scholarsLoading}
                        notFoundContent={scholarsLoading ? <Spin size="small" /> : "No scholars found"}
                      >
                        {scholars && scholars.length > 0 ? (
                          scholars.map(scholar => (
                            <Select.Option 
                              value={scholar.scholar_id} 
                              key={scholar.scholar_id || scholar.key}
                            >
                              {scholar.name || `${scholar.firstName || ''} ${scholar.lastName || ''}`}
                            </Select.Option>
                          ))
                        ) : (
                          <Select.Option value="" disabled>No scholars available</Select.Option>
                        )}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="dateReceived"
                      label="Date Received"
                      rules={[{ required: true, message: 'Please select date received' }]}
                    >
                      <DatePicker 
                        style={{ width: '100%' }} 
                        format="YYYY-MM-DD"
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="amount"
                      label="Amount"
                      rules={[
                        { required: true, message: 'Please enter amount' },
                        {
                          validator: (_, value) => {
                            // Parse value to handle formatted input with commas
                            const parsedValue = parseFloat(String(value).replace(/[^0-9.]/g, ''));
                            
                            if (isNaN(parsedValue)) {
                              return Promise.reject('Amount must be a valid number');
                            }
                            if (parsedValue <= 0) {
                              return Promise.reject('Amount must be greater than 0');
                            }
                            return Promise.resolve();
                          }
                        }
                      ]}
                    >
                      <InputNumber 
                        style={{ width: '100%' }}
                        placeholder="Enter amount"
                        formatter={value => `₱ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                        parser={value => {
                          // Strip all non-numeric characters except for commas and decimal points
                          const parsed = value.replace(/₱\s?/g, '');
                          // Only return the value if it's a valid number or empty
                          const num = parseFloat(parsed.replace(/,/g, ''));
                          return isNaN(num) ? '' : num;
                        }}
                        min={0.01}
                        step={0.01}
                        precision={2}
                        stringMode={true}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col span={24}>
                    <Form.Item
                      name="description"
                      label="Description"
                      rules={[{ required: true, message: 'Please enter description' }]}
                    >
                      <Input 
                        placeholder="Enter description"
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item className="form-buttons">
                  <Button type="primary" htmlType="submit" loading={loading}>
                    Save
                  </Button>
                </Form.Item>
              </Form>
            </div>
          </Modal>

          {/* View Receipt Modal */}
          <Modal
            title="Acknowledgement Receipt"
            open={isViewModalVisible}
            onCancel={handleViewModalCancel}
            closeIcon={<CloseOutlined />}
            destroyOnClose={true}
            keyboard={true}
            maskClosable={true}
            getContainer={document.body}
            className="receipt-view-modal"
            width={700}
            style={{ 
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              margin: 0
            }}
            footer={[
              selectedReceipt && selectedReceipt.status === 'For Signature' && (
                <Button key="delete" danger icon={<DeleteOutlined />} onClick={() => handleDelete(selectedReceipt)}>
                  Delete
                </Button>
              ),
              <Button 
                key="download" 
                type="primary" 
                icon={<FileTextOutlined />} 
                onClick={handleDownloadPDF}
                disabled={selectedReceipt?.status === 'For Signature'}
              >
                Download as PDF
              </Button>
            ]}
          >
            {selectedReceipt && (
              <div className="receipt-document-wrapper" style={{ 
                maxHeight: window.innerWidth >= 1024 ? 'unset' : 'calc(80vh - 120px)', 
                overflow: window.innerWidth >= 1024 ? 'visible' : 'auto',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none'
              }}>
                <div className="receipt-document" ref={receiptRef}>
                  <div className="ars-company-header">
                    <img 
                      src={process.env.PUBLIC_URL + '/images/MYT Logo.png'} 
                      alt="MYT SoftDev Solutions, Inc." 
                      className="ars-company-logo"
                      crossOrigin="anonymous"
                    />
                    <div className="ars-company-info">
                      <h1 className="ars-company-name">MYT SoftDev Solutions, Inc.</h1>
                      <p className="ars-company-address">301 The Greenery, Pope John Paul II Ave, Cebu City, 6000 Cebu</p>
                    </div>
                  </div>

                  <div className="ars-receipt-title">
                    <h2>ACKNOWLEDGEMENT RECEIPT</h2>
                  </div>
                  
                  <div className="ars-receipt-content">
                    <p className="ars-receipt-text">
                      I, <span className="ars-scholar-inline">{selectedReceipt.name}</span>, the undersigned scholar of MYT SoftDev Solutions, Inc., acknowledge the receipt 
                      of our <span className="ars-blank-line">{selectedReceipt.description || selectedReceipt.type || 'Monthly Allowance'}</span> worth <span className="ars-blank-line">{formatCurrency(selectedReceipt.amount)}</span> received on <span className="ars-blank-line">
                        {selectedReceipt.date_received ? 
                          (selectedReceipt.date_received.includes('T') || selectedReceipt.date_received.includes(':')) ? 
                            dayjs(selectedReceipt.date_received).format('MMMM D, YYYY') : 
                            selectedReceipt.date_received 
                          : 'pending'}
                      </span>.
                    </p>
                  </div>
                  
                  <div className="ars-signature-section">
                    <p className="ars-acknowledged-by">Acknowledged by:</p>
                    <div className="ars-signature-container" style={{
                      margin: '0',
                      padding: '0',
                      display: 'block',
                      width: '100%'
                    }}>
                      <div className="ars-signature-box" style={{ 
                        border: '1px solid #e8e8e8', 
                        borderRadius: '4px',
                        backgroundColor: '#ffffff',
                        boxShadow: 'inset 0 0 3px rgba(0, 0, 0, 0.05)',
                        display: 'inline-block',
                        width: isExtraSmallMobile ? '160px' : isSmallMobile ? '160px' : isMobile ? '160px' : '300px',
                        height: isExtraSmallMobile ? '160px' : isSmallMobile ? '160px' : isMobile ? '160px' : '200px',
                        aspectRatio: '1/1',
                        margin: '0',
                        marginLeft: '0',
                        overflow: 'hidden',
                        position: 'relative'
                      }}>
                        {selectedReceipt.signature && (
                          <img 
                            src={selectedReceipt.signature} 
                            alt="Scholar Signature" 
                            style={{ 
                              maxWidth: '100%',
                              maxHeight: '100%',
                              objectFit: 'contain',
                              margin: '0'
                            }}
                          />
                        )}
                      </div>
                      <div className="ars-name-container" style={{
                        width: isExtraSmallMobile ? '160px' : isSmallMobile ? '160px' : isMobile ? '160px' : '300px',
                        margin: '10px 0 0 0',
                        textAlign: 'left',
                        borderTop: '1px solid #000',
                        paddingTop: '5px'
                      }}>
                        <p className="ars-scholar-name">{selectedReceipt.name}</p>
                        <p className="ars-scholar-title">Scholar</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </Modal>
        </div>
        
        {/* Copyright Footer - positioned at bottom */}
        <div className="copyright-footer">
          <span>©2025 MYT Softdev Solutions Inc.</span>
        </div>
      </Content>
    </Layout>
  );
};

export default AdminAcknowledgementReceipt; 